import type {
  DestinationCapability,
  DestinationComponentCategory,
  DestinationComponentDefinition,
  DestinationFallback,
  DestinationJsonSchema,
  DestinationManifest,
  DestinationPlacement,
} from './types'
import { LE_VANILLA_DEFAULT_LAYOUT } from '../templates/le_vanilla/defaultLayout'
import { LE_VANILLA_MAP_DEFAULT_LAYOUT } from '../templates/le_vanilla_map/defaultLayout'

const emptyObjectSchema: DestinationJsonSchema = {
  type: 'object',
  properties: {},
  additionalProperties: false,
}

const semanticKeySchema: DestinationJsonSchema = {
  type: 'string',
  minLength: 1,
  maxLength: 64,
  pattern: '^[a-z0-9]+(?:-[a-z0-9]+)*$',
}

interface DefinitionInput {
  name: string
  displayName?: string
  description: string
  category: DestinationComponentCategory
  placements: DestinationPlacement[]
  capabilities?: DestinationCapability[]
  configSchema?: DestinationJsonSchema
  liveStateSchema?: DestinationJsonSchema
  defaultConfig?: DestinationComponentDefinition['defaultConfig']
  controls?: DestinationComponentDefinition['editor']['controls']
  icon: string
  useWhen: string
  avoidWhen?: string
  fallback?: DestinationFallback
}

function defineComponent(input: DefinitionInput): DestinationComponentDefinition {
  return {
    name: input.name,
    version: 1,
    category: input.category,
    placements: input.placements,
    configSchema: input.configSchema ?? emptyObjectSchema,
    liveStateSchema: input.liveStateSchema,
    defaultConfig: input.defaultConfig ?? {},
    requiredCapabilities: input.capabilities ?? [],
    editor: {
      displayName: input.displayName ?? input.name,
      description: input.description,
      icon: input.icon,
      controls: input.controls ?? [],
    },
    ai: {
      useWhen: input.useWhen,
      avoidWhen: input.avoidWhen,
    },
    fallback: input.fallback ?? 'hide',
  }
}

export const DESTINATION_COMPONENT_ALIASES: Record<string, string> = {
  MainPlayer: 'PrimaryView',
  Title: 'EventTitle',
  Description: 'EventDescription',
  RaceLeaders: 'ResultsView',
  MobileModeSwitcher: 'ViewSwitcher',
}

export const DESTINATION_COMPONENT_DEFINITIONS: DestinationComponentDefinition[] = [
  defineComponent({
    name: 'Topbar',
    description: 'Destination navigation and event branding.',
    category: 'event',
    placements: ['navigation'],
    capabilities: ['event.v1', 'viewer-session.v1'],
    icon: 'panel-top',
    useWhen: 'The destination needs its standard top navigation.',
  }),
  defineComponent({
    name: 'PrimaryView',
    description: 'The viewer-selected or producer-featured camera.',
    category: 'media',
    placements: ['primary-media'],
    capabilities: ['cameras.v1'],
    configSchema: {
      type: 'object',
      properties: {
        showFeaturedChip: {type: 'boolean', default: true},
        showName: {type: 'boolean', default: true},
        showWatchingCount: {type: 'boolean', default: true},
      },
      additionalProperties: false,
    },
    defaultConfig: {
      showFeaturedChip: true,
      showName: true,
      showWatchingCount: true,
    },
    controls: [
      {key: 'showFeaturedChip', label: 'Featured indicator', control: 'boolean'},
      {key: 'showName', label: 'Camera name', control: 'boolean'},
      {key: 'showWatchingCount', label: 'Viewer count', control: 'boolean'},
    ],
    icon: 'play',
    useWhen: 'The destination needs one dominant live camera.',
    fallback: 'empty-state',
  }),
  defineComponent({
    name: 'EventTitle',
    description: 'The event name and subtitle.',
    category: 'event',
    placements: ['event-metadata'],
    capabilities: ['event.v1'],
    configSchema: {
      type: 'object',
      properties: {text: {type: 'string', maxLength: 160}},
      additionalProperties: false,
    },
    controls: [{key: 'text', label: 'Display title', control: 'text'}],
    icon: 'heading',
    useWhen: 'The event identity needs to be visible.',
  }),
  defineComponent({
    name: 'EventDescription',
    description: 'The public event description.',
    category: 'event',
    placements: ['event-metadata'],
    capabilities: ['event.v1'],
    configSchema: {
      type: 'object',
      properties: {showMore: {type: 'boolean', default: true}},
      additionalProperties: false,
    },
    defaultConfig: {showMore: true},
    controls: [{key: 'showMore', label: 'Show more action', control: 'boolean'}],
    icon: 'text',
    useWhen: 'Viewers need event context or editorial copy.',
    fallback: 'hide',
  }),
  defineComponent({
    name: 'ProducerRow',
    description: 'Public host identity, verification, and follow affordance.',
    category: 'event',
    placements: ['event-metadata'],
    capabilities: ['event.v1', 'viewer-session.v1'],
    configSchema: {
      type: 'object',
      properties: {
        showFollow: {type: 'boolean', default: true},
        followLabel: {type: 'string', maxLength: 40},
      },
      additionalProperties: false,
    },
    defaultConfig: {showFollow: true},
    controls: [
      {key: 'showFollow', label: 'Follow action', control: 'boolean'},
      {key: 'followLabel', label: 'Follow label', control: 'text'},
    ],
    icon: 'badge-check',
    useWhen: 'The event producer should receive visible attribution.',
  }),
  defineComponent({
    name: 'EngagementCluster',
    description: 'Viewer interaction actions.',
    category: 'interaction',
    placements: ['interaction', 'event-metadata'],
    capabilities: ['viewer-session.v1'],
    configSchema: {
      type: 'object',
      properties: {
        hideHeart: {type: 'boolean', default: false},
        likeCount: {type: 'string', maxLength: 16},
      },
      additionalProperties: false,
    },
    defaultConfig: {hideHeart: false},
    controls: [
      {key: 'hideHeart', label: 'Hide like action', control: 'boolean'},
      {key: 'likeCount', label: 'Like count', control: 'text'},
    ],
    icon: 'heart',
    useWhen: 'The destination should expose viewer engagement actions.',
    fallback: 'hide',
  }),
  defineComponent({
    name: 'CameraRail',
    description: 'An ordered list of public live cameras.',
    category: 'media',
    placements: ['secondary-media'],
    capabilities: ['cameras.v1'],
    configSchema: {
      type: 'object',
      properties: {
        sortBy: {type: 'string', enum: ['position', 'name'], default: 'position'},
      },
      additionalProperties: false,
    },
    defaultConfig: {sortBy: 'position'},
    controls: [{
      key: 'sortBy',
      label: 'Sort cameras',
      control: 'select',
      options: [
        {label: 'Route position', value: 'position'},
        {label: 'Name', value: 'name'},
      ],
    }],
    icon: 'list-video',
    useWhen: 'Viewers should be able to choose among event cameras.',
    fallback: 'empty-state',
  }),
  defineComponent({
    name: 'SponsorLockup',
    description: 'A restrained sponsor label and logo.',
    category: 'event',
    placements: ['event-metadata'],
    capabilities: ['event.v1', 'live-content.v1'],
    configSchema: {
      type: 'object',
      properties: {
        label: {type: 'string', maxLength: 80},
        name: {type: 'string', maxLength: 120},
        logoUrl: {
          type: 'string',
          maxLength: 2048,
          pattern: '^(?:https?://|/)',
        },
        href: {
          type: 'string',
          maxLength: 2048,
          pattern: '^(?:https?://|/)',
        },
      },
      additionalProperties: false,
    },
    liveStateSchema: {
      type: 'object',
      properties: {
        visible: {type: 'boolean'},
        message: {type: 'string', maxLength: 160},
      },
      additionalProperties: false,
    },
    controls: [
      {key: 'label', label: 'Label', control: 'text'},
      {key: 'name', label: 'Sponsor name', control: 'text'},
      {key: 'logoUrl', label: 'Logo URL', control: 'text'},
      {key: 'href', label: 'Destination URL', control: 'text'},
    ],
    icon: 'badge',
    useWhen: 'A sponsor should receive event-level attribution.',
    avoidWhen: 'No sponsor assets or agreement exist.',
  }),
  defineComponent({
    name: 'ResultsView',
    description: 'Provider-neutral live standings and race results.',
    category: 'results',
    placements: ['results'],
    capabilities: ['results.v1', 'live-content.v1'],
    configSchema: {
      type: 'object',
      properties: {
        sourceKey: semanticKeySchema,
        variant: {type: 'string', enum: ['compact', 'table'], default: 'compact'},
        limit: {type: 'integer', minimum: 1, maximum: 100, default: 5},
        label: {type: 'string', maxLength: 80},
        title: {type: 'string', maxLength: 80},
        emptyText: {type: 'string', maxLength: 240},
        tabs: {
          type: 'array',
          maxItems: 12,
          items: {
            type: 'object',
            required: ['key', 'label', 'entries'],
            properties: {
              key: {type: 'string', minLength: 1, maxLength: 64},
              label: {type: 'string', minLength: 1, maxLength: 80},
              entries: {
                type: 'array',
                maxItems: 100,
                items: {
                  type: 'object',
                  required: ['pos', 'name', 'time'],
                  properties: {
                    pos: {type: 'integer', minimum: 1},
                    name: {type: 'string', minLength: 1, maxLength: 120},
                    bib: {type: 'string', maxLength: 32},
                    gap: {type: 'string', maxLength: 32},
                    time: {type: 'string', maxLength: 32},
                  },
                  additionalProperties: false,
                },
              },
            },
            additionalProperties: false,
          },
        },
      },
      additionalProperties: false,
    },
    liveStateSchema: {
      type: 'object',
      properties: {
        selectedCategory: {type: 'string', maxLength: 80},
      },
      additionalProperties: false,
    },
    defaultConfig: {variant: 'compact', limit: 5},
    controls: [
      {key: 'sourceKey', label: 'Results source', control: 'source'},
      {
        key: 'variant',
        label: 'Presentation',
        control: 'select',
        options: [
          {label: 'Compact', value: 'compact'},
          {label: 'Table', value: 'table'},
        ],
      },
      {key: 'limit', label: 'Entries', control: 'number'},
      {key: 'label', label: 'Heading', control: 'text'},
    ],
    icon: 'list-ordered',
    useWhen: 'A configured results provider can show standings.',
    avoidWhen: 'No results source is connected to the event.',
    fallback: 'empty-state',
  }),
  defineComponent({
    name: 'RouteMap',
    description: 'Event route, live entities, and camera positions.',
    category: 'tracking',
    placements: ['tracking'],
    capabilities: ['event.v1', 'cameras.v1', 'tracking.v1'],
    configSchema: {
      type: 'object',
      properties: {
        sourceKey: semanticKeySchema,
        basemap: {
          type: 'string',
          enum: ['standard', 'satellite'],
          default: 'satellite',
        },
      },
      additionalProperties: false,
    },
    defaultConfig: {basemap: 'satellite'},
    controls: [
      {key: 'sourceKey', label: 'Tracking source', control: 'source'},
      {
        key: 'basemap',
        label: 'Basemap',
        control: 'select',
        options: [
          {label: 'Standard', value: 'standard'},
          {label: 'Satellite', value: 'satellite'},
        ],
      },
    ],
    icon: 'map',
    useWhen: 'The event has a route or live tracking data.',
    fallback: 'empty-state',
  }),
  defineComponent({
    name: 'ViewSwitcher',
    description: 'Responsive navigation among destination surface modes.',
    category: 'interaction',
    placements: ['navigation'],
    configSchema: {
      type: 'object',
      properties: {
        modes: {
          type: 'array',
          items: {type: 'string', enum: ['about', 'cameras', 'map']},
          minItems: 1,
          maxItems: 3,
        },
        defaultMode: {type: 'string', enum: ['about', 'cameras', 'map']},
      },
      required: ['modes'],
      additionalProperties: false,
    },
    defaultConfig: {modes: ['about', 'cameras'], defaultMode: 'about'},
    icon: 'panel-bottom',
    useWhen: 'A responsive shell needs mobile surface navigation.',
  }),
  defineComponent({
    name: 'CleanViewButton',
    description: 'Temporarily hides player overlays.',
    category: 'interaction',
    placements: ['overlay', 'interaction'],
    icon: 'eye-off',
    useWhen: 'The primary player exposes overlays.',
  }),
  defineComponent({
    name: 'FullscreenButton',
    description: 'Toggles fullscreen playback.',
    category: 'interaction',
    placements: ['overlay', 'interaction'],
    configSchema: {
      type: 'object',
      properties: {label: {type: 'string', maxLength: 80}},
      additionalProperties: false,
    },
    controls: [{key: 'label', label: 'Accessible label', control: 'text'}],
    icon: 'maximize',
    useWhen: 'A media surface can be viewed fullscreen.',
  }),
  defineComponent({
    name: 'CloseButton',
    description: 'Closes an immersive destination surface.',
    category: 'interaction',
    placements: ['navigation', 'overlay'],
    configSchema: {
      type: 'object',
      properties: {label: {type: 'string', maxLength: 80}},
      additionalProperties: false,
    },
    controls: [{key: 'label', label: 'Accessible label', control: 'text'}],
    icon: 'x',
    useWhen: 'The destination is rendered in a dismissible context.',
  }),
  defineComponent({
    name: 'StickyPlayer',
    description: 'Mobile sticky rendering of the current camera.',
    category: 'media',
    placements: ['primary-media'],
    capabilities: ['cameras.v1'],
    configSchema: {
      type: 'object',
      properties: {showWatchingCount: {type: 'boolean', default: true}},
      additionalProperties: false,
    },
    defaultConfig: {showWatchingCount: true},
    controls: [{
      key: 'showWatchingCount',
      label: 'Viewer count',
      control: 'boolean',
    }],
    icon: 'picture-in-picture',
    useWhen: 'The shell needs a mobile sticky player.',
    fallback: 'empty-state',
  }),
  defineComponent({
    name: 'EmptyState',
    description: 'A consistent pre-event or unavailable-media state.',
    category: 'event',
    placements: ['primary-media', 'secondary-media', 'results', 'tracking'],
    capabilities: ['event.v1'],
    configSchema: {
      type: 'object',
      properties: {
        state: {
          type: 'string',
          enum: ['pre-event', 'awaiting-cameras', 'off-air', 'unavailable'],
          default: 'pre-event',
        },
      },
      additionalProperties: false,
    },
    defaultConfig: {state: 'pre-event'},
    controls: [{
      key: 'state',
      label: 'State',
      control: 'select',
      options: [
        {label: 'Pre-event', value: 'pre-event'},
        {label: 'Awaiting cameras', value: 'awaiting-cameras'},
        {label: 'Off air', value: 'off-air'},
        {label: 'Unavailable', value: 'unavailable'},
      ],
    }],
    icon: 'circle-off',
    useWhen: 'A deliberate empty destination state is needed.',
  }),
  defineComponent({
    name: 'MultiView',
    description: 'A provider-driven presentation of multiple live cameras.',
    category: 'media',
    placements: ['primary-media', 'secondary-media'],
    capabilities: ['cameras.v1'],
    configSchema: {
      type: 'object',
      properties: {
        variant: {
          type: 'string',
          enum: ['grid', 'split', 'pictureInPicture', 'focused'],
          default: 'grid',
        },
        selection: {
          type: 'string',
          enum: ['featured', 'viewer-selected', 'all-online', 'explicit'],
          default: 'all-online',
        },
        identities: {
          type: 'array',
          items: {type: 'string', minLength: 1, maxLength: 255},
          maxItems: 16,
        },
        limit: {type: 'integer', minimum: 2, maximum: 16, default: 4},
      },
      additionalProperties: false,
    },
    defaultConfig: {variant: 'grid', selection: 'all-online', limit: 4},
    controls: [
      {
        key: 'variant',
        label: 'Layout',
        control: 'select',
        options: [
          {label: 'Grid', value: 'grid'},
          {label: 'Split', value: 'split'},
          {label: 'Picture in picture', value: 'pictureInPicture'},
          {label: 'Focused', value: 'focused'},
        ],
      },
      {
        key: 'selection',
        label: 'Camera selection',
        control: 'select',
        options: [
          {label: 'All online', value: 'all-online'},
          {label: 'Featured', value: 'featured'},
          {label: 'Viewer selected', value: 'viewer-selected'},
          {label: 'Explicit list', value: 'explicit'},
        ],
      },
      {key: 'limit', label: 'Camera limit', control: 'number'},
    ],
    icon: 'layout-grid',
    useWhen: 'Several live cameras should be visible simultaneously.',
    avoidWhen: 'Only one public camera is expected.',
    fallback: 'empty-state',
  }),
]

export const DESTINATION_MANIFEST: DestinationManifest = {
  manifestVersion: 1,
  layoutVersion: 1,
  aliases: DESTINATION_COMPONENT_ALIASES,
  components: DESTINATION_COMPONENT_DEFINITIONS,
  shells: [
    {
      name: 'le_vanilla',
      slots: {
        topbar: ['navigation'],
        player: ['primary-media'],
        player_overlay: ['overlay', 'interaction'],
        below_player: ['event-metadata', 'interaction'],
        sidebar: ['secondary-media', 'event-metadata', 'results'],
        mobile_switcher: ['navigation'],
      },
    },
    {
      name: 'le_vanilla_map',
      slots: {
        topbar: ['navigation'],
        player: ['primary-media'],
        map: ['tracking'],
        player_overlay: ['overlay', 'interaction'],
        below_player: ['event-metadata', 'interaction'],
        sidebar: ['secondary-media', 'event-metadata', 'results'],
        mobile_switcher: ['navigation'],
      },
    },
  ],
  templates: {
    none: {
      name: 'Stream only',
      hasViewerPage: false,
      defaultLayout: LE_VANILLA_DEFAULT_LAYOUT,
    },
    le_vanilla: {
      name: 'Le Vanilla',
      hasViewerPage: true,
      defaultLayout: LE_VANILLA_DEFAULT_LAYOUT,
    },
    le_vanilla_map: {
      name: 'Le Vanilla + Map',
      hasViewerPage: true,
      defaultLayout: LE_VANILLA_MAP_DEFAULT_LAYOUT,
    },
  },
}

export function canonicalDestinationComponentName(name: string): string {
  return DESTINATION_COMPONENT_ALIASES[name] ?? name
}

export function destinationComponentDefinition(
  name: string,
): DestinationComponentDefinition | null {
  const canonical = canonicalDestinationComponentName(name)
  return (
    DESTINATION_COMPONENT_DEFINITIONS.find(
      (definition) => definition.name === canonical,
    ) ?? null
  )
}
